import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Target, Cpu, Award, LifeBuoy, Handshake, ArrowRight, MapPin, Sparkles,
  Quote, CheckCircle2,
} from 'lucide-react';
import ColorAmbient from '../components/ColorAmbient';
import RegistrationMark from '../components/RegistrationMark';
import BrandLogo from '../components/BrandLogo';
import { companyStory, teamMembers, stats } from '../data/mock';

// ProofNation brand palette — derived from official logo colors.
const BRAND_STRIP = ['#0A2A63', '#1E4CB8', '#4A76D6', '#FF4E50', '#E03B3D'];
const PILLAR_ACCENTS = ['#0A2A63', '#1E4CB8', '#FF4E50', '#E03B3D'];
const PILLAR_ICONS = [Cpu, Award, LifeBuoy, Handshake];

const ColorStrip = ({ className = '' }) => (
  <div className={`flex h-1.5 w-40 overflow-hidden rounded-full ${className}`} aria-hidden>
    {BRAND_STRIP.map((c) => (
      <span key={c} className="flex-1" style={{ backgroundColor: c }} />
    ))}
  </div>
);

const StatBlock = ({ value, label, testId }) => (
  <div className="text-center" data-testid={testId}>
    <p className="text-5xl md:text-6xl font-black text-white tracking-tight">{value}</p>
    <p className="mt-2 text-xs md:text-sm uppercase tracking-[0.2em] text-slate-400 font-semibold">{label}</p>
  </div>
);

const TeamCard = ({ member }) => (
  <article
    className="group relative bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-lg hover:shadow-2xl btn-transition hover:-translate-y-1.5"
    data-testid={`team-card-${member.id}`}
  >
    <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
      <img
        src={member.photo}
        alt={`${member.name} — ${member.role}`}
        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
      />
      <span className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#050F2E]/80 to-transparent" />
      <span
        className="absolute top-4 left-4 h-2.5 w-2.5 rounded-full ring-4 ring-white/70"
        style={{ backgroundColor: member.accent }}
      />
    </div>
    <div className="p-6">
      <h3 className="text-xl font-black text-[#050F2E] tracking-tight">{member.name}</h3>
      <p
        className="mt-1 text-xs font-bold uppercase tracking-[0.18em]"
        style={{ color: member.accent }}
      >
        {member.role}
      </p>
      <p className="mt-3 text-slate-600 leading-relaxed">{member.bio}</p>
    </div>
  </article>
);

const AboutUs = () => {
  return (
    <main className="bg-white min-h-screen" data-testid="about-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-white pt-32 pb-20">
        <ColorAmbient variant="cool" intensity="medium" />
        <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
        <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

        <div className="container-pn relative max-w-4xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#0A2A63]/10 text-[#0A2A63] mb-7 shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#FF4E50]" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" data-testid="about-eyebrow">
              {companyStory.eyebrow}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-[#050F2E]" data-testid="about-headline">
            We don&apos;t just sell printing technology.{' '}
            <span className="pn-gradient-text">
              We make it work for you.
            </span>
          </h1>
          <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-3xl">
            {companyStory.intro}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-6">
            <ColorStrip />
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="h-4 w-4 text-[#FF4E50]" />
              <span className="text-sm font-medium">Proudly USA based · serving printers nationwide</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MISSION ============ */}
      <section className="relative py-24 bg-white overflow-hidden" data-testid="about-mission">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 85% 15%, rgba(255,78,80,0.06), transparent 55%),' +
              'radial-gradient(ellipse at 10% 90%, rgba(10,42,99,0.07), transparent 55%)',
          }}
        />
        <div className="container-pn relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2 flex lg:justify-center">
            <span className="h-16 w-16 rounded-2xl bg-[#0A2A63] flex items-center justify-center shadow-xl">
              <Target className="h-8 w-8 text-[#FF4E50]" />
            </span>
          </div>
          <div className="lg:col-span-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-4">Our Mission</p>
            <p className="text-2xl md:text-4xl font-black text-[#050F2E] leading-tight tracking-tight">
              {companyStory.mission}
            </p>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-3xl">
              ProofNation brings decades of hands-on printing expertise together with some of the industry&apos;s
              most advanced color management, proofing and measurement technologies. We don&apos;t simply deliver
              products — we help you select, implement, optimize and support the right solution for your production
              environment.
            </p>
          </div>
        </div>
      </section>

      {/* ============ STATS BAND ============ */}
      <section className="relative bg-[#0A2A63] py-16 overflow-hidden" data-testid="about-stats">
        <div className="absolute top-0 left-0 right-0 h-1.5 flex" aria-hidden>
          {BRAND_STRIP.map((c) => <span key={c} className="flex-1" style={{ backgroundColor: c }} />)}
        </div>
        <div className="container-pn grid grid-cols-1 sm:grid-cols-3 gap-10">
          <StatBlock value="100+" label="Years combined experience" testId="stat-years" />
          <StatBlock value={`${stats.customers}+`} label="Happy customers" testId="stat-customers" />
          <StatBlock value="#1" label="Technical support in the U.S." testId="stat-support" />
        </div>
      </section>

      {/* ============ WHAT WE DO ============ */}
      <section className="py-24 bg-slate-50/70" data-testid="about-what-we-do">
        <div className="container-pn">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">What We Do</p>
            <h2 className="text-3xl md:text-4xl font-black text-[#050F2E] tracking-tight leading-tight">
              Better color. Smarter workflows. Greater efficiency.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              {companyStory.whatWeDo}
            </p>
          </div>

          <div className="mt-12">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mb-5">The brands we work with</p>
            <div className="flex flex-wrap items-center gap-4">
              <BrandLogo brand="printfactory" chip imgClassName="h-9" chipClassName="px-5 py-3" />
              <BrandLogo brand="barbieri" chip imgClassName="h-9" chipClassName="px-5 py-3" />
              <BrandLogo brand="nix" chip imgClassName="h-9" chipClassName="px-5 py-3" />
              <BrandLogo brand="impoproof" chip imgClassName="h-9" chipClassName="px-5 py-3" testId="whatwedo-logo-impoproof" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY US ============ */}
      <section className="relative py-24 bg-[#050F2E] text-white overflow-hidden" data-testid="about-why-us">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#FF4E50]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[#1E4CB8]/20 blur-3xl pointer-events-none" />
        <div className="container-pn relative">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Why Us</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
              Expertise. Support. Results.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              With 100+ years of combined industry experience, our team understands printing from the inside out.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {companyStory.whyUs.map((pillar, i) => {
              const Icon = PILLAR_ICONS[i];
              const accent = PILLAR_ACCENTS[i] || '#FF4E50';
              return (
                <div
                  key={pillar.title}
                  className="relative rounded-2xl bg-white/5 border border-white/10 p-7 hover:bg-white/[0.08] transition-colors"
                  data-testid={`why-us-${i}`}
                >
                  <span
                    className="inline-flex h-12 w-12 rounded-xl items-center justify-center mb-5"
                    style={{ backgroundColor: `${accent}22`, color: accent }}
                  >
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="text-lg font-black tracking-tight">{pillar.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{pillar.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TEAM / WHO WE ARE ============ */}
      <section className="py-24 bg-white" data-testid="about-team">
        <div className="container-pn">
          <div className="max-w-3xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Who We Are</p>
            <h2 className="text-3xl md:text-5xl font-black text-[#050F2E] tracking-tight leading-tight">
              The people behind ProofNation
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              A team that has lived every side of the pressroom — technology is only part of the equation. The rest
              is people who know how to make it work.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <TeamCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="pb-28 bg-white" data-testid="about-cta">
        <div className="container-pn">
          <div className="relative overflow-hidden rounded-3xl bg-[#0A2A63] px-8 py-14 md:px-16 md:py-16">
            <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#FF4E50]/25 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#1E4CB8]/30 blur-3xl pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <Quote className="h-9 w-9 text-[#FF4E50] mb-4" />
                <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-tight">
                  Smart technology. Support you can count on. Results.
                </h2>
                <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
                  Industry-leading technology. Real printing expertise. Personalized technical support.
                  One trusted partner.
                </p>
                <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
                  {['Expertise', 'Support', 'Results'].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-white font-semibold">
                      <CheckCircle2 className="h-5 w-5 text-[#FF4E50]" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-stretch">
                <Link to="/contact" className="w-full">
                  <Button
                    className="w-full bg-[#FF4E50] hover:bg-[#E03B3D] text-white rounded-full h-12 px-7 font-bold btn-transition group shadow-lg shadow-[#FF4E50]/30"
                    data-testid="about-cta-contact"
                  >
                    Talk to a specialist
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/shop" className="w-full">
                  <Button
                    variant="outline"
                    className="w-full bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full h-12 px-7 font-bold btn-transition"
                    data-testid="about-cta-shop"
                  >
                    Explore products
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutUs;
